import React from 'react';
import PropTypes from 'prop-types';
import { Link, selectSystemProps, StackView, Typography, useTheme, useThemeTokens, useResponsiveThemeTokens, createMediaQueryStyles, withLinkRouter, useViewport, getTokensPropType, StyleSheet } from '@telus-uds/components-base';
import styled from 'styled-components';
import { htmlAttrs, transformGradient } from '../utils';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([htmlAttrs]);
const BlockQuoteContainer = /*#__PURE__*/styled.blockquote.withConfig({
  displayName: "BlockQuote__BlockQuoteContainer",
  componentId: "components-web__sc-vals3u-0"
})(["margin:0;position:relative;padding-left:", ";padding-right:", ";padding-bottom:", ";padding-top:", ";&::before{content:'';left:0;top:0;position:absolute;height:100%;width:", ";background:", ";}"], _ref => {
  let {
    paddingLeft
  } = _ref;
  return `${paddingLeft}px`;
}, _ref2 => {
  let {
    paddingRight
  } = _ref2;
  return `${paddingRight}px`;
}, _ref3 => {
  let {
    paddingBottom
  } = _ref3;
  return `${paddingBottom}px`;
}, _ref4 => {
  let {
    paddingTop
  } = _ref4;
  return `${paddingTop}px`;
}, _ref5 => {
  let {
    width
  } = _ref5;
  return `${width}px`;
}, _ref6 => {
  let {
    backgroundGradient
  } = _ref6;
  return backgroundGradient && transformGradient(backgroundGradient);
});
const QuoteContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "BlockQuote__QuoteContainer",
  componentId: "components-web__sc-vals3u-1"
})(["margin-bottom:", ";"], _ref7 => {
  let {
    marginBottom
  } = _ref7;
  return `${marginBottom}px`;
});
const selectTitleHeadingTokens = themeTokens => ({
  fontSize: themeTokens.titleHeadingFontSize,
  fontName: themeTokens.titleHeadingFontName,
  fontWeight: themeTokens.titleHeadingFontWeight,
  lineHeight: themeTokens.titleHeadingLineHeight
});
const BlockQuote = /*#__PURE__*/React.forwardRef((_ref8, ref) => {
  let {
    children,
    link,
    additionalInfo,
    linkHref,
    textStyle = 'large',
    LinkRouter,
    linkRouterProps,
    tokens,
    variant,
    ...rest
  } = _ref8;
  const viewport = useViewport();
  const {
    themeOptions: {
      enableMediaQueryStyleSheet
    }
  } = useTheme();
  const useTokens = enableMediaQueryStyleSheet ? useResponsiveThemeTokens : useThemeTokens;
  const resolvedTokens = useTokens('BlockQuote', tokens, variant, !enableMediaQueryStyleSheet && {
    viewport
  });
  const themeTokens = enableMediaQueryStyleSheet ? resolvedTokens[viewport] : resolvedTokens;
  const {
    color,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    marginBottom,
    width,
    backgroundGradient,
    titleHeadingFontSize,
    titleHeadingFontName,
    titleHeadingFontWeight,
    titleHeadingLineHeight,
    titleFontSize,
    titleFontName,
    titleFontWeight,
    titleLineHeight,
    linkFontSize,
    linkFontName,
    linkFontWeight,
    linkLineHeight
  } = themeTokens;
  let titleHeadingStyles;
  let titleHeadingMediaIds;
  if (enableMediaQueryStyleSheet) {
    const {
      transformedTitleHeadingThemeTokens
    } = Object.entries(themeTokens).reduce((acc, _ref9) => {
      let [vp, viewportTokens] = _ref9;
      acc.transformedTitleHeadingThemeTokens[vp] = {
        fontSize: viewportTokens.titleHeadingFontSize,
        fontName: viewportTokens.titleHeadingFontName,
        fontWeight: viewportTokens.titleHeadingFontWeight,
        lineHeight: viewportTokens.titleHeadingLineHeight
      };
      return acc;
    }, {
      transformedTitleHeadingThemeTokens: {}
    });
    const titleHeadingMediaQueryStyles = createMediaQueryStyles(transformedTitleHeadingThemeTokens);
    const {
      ids,
      styles
    } = StyleSheet.create({
      titleHeading: {
        ...selectTitleHeadingTokens(themeTokens),
        ...titleHeadingMediaQueryStyles
      }
    });
    titleHeadingStyles = styles.titleHeading;
    titleHeadingMediaIds = ids.titleHeading;
  } else {
    titleHeadingStyles = {
      fontSize: titleHeadingFontSize,
      fontName: titleHeadingFontName,
      fontWeight: titleHeadingFontWeight,
      lineHeight: titleHeadingLineHeight
    };
  }
  const mappedTextSize = textStyle === 'heading' ? 'h3' : textStyle;
  const titleTokens = textStyle === 'heading' ? titleHeadingStyles : {
    fontSize: titleFontSize,
    fontName: titleFontName,
    fontWeight: titleFontWeight,
    lineHeight: titleLineHeight
  };
  const renderLink = () => {
    if (linkHref) {
      return /*#__PURE__*/_jsx(Link, {
        href: linkHref,
        tokens: {
          blockFontSize: linkFontSize,
          blockFontName: linkFontName,
          blockFontWeight: linkFontWeight,
          blockLineHeight: linkLineHeight,
          color
        },
        variant: {
          alternative: true
        },
        LinkRouter: LinkRouter,
        linkRouterProps: linkRouterProps,
        children: link
      });
    }
    return /*#__PURE__*/_jsx(Typography, {
      tokens: {
        color,
        fontName: linkFontName,
        fontSize: linkFontSize,
        fontWeight: linkFontWeight,
        lineHeight: linkLineHeight
      },
      children: link
    });
  };
  const renderQuote = () => {
    const quote = /*#__PURE__*/_jsx(Typography, {
      tokens: {
        color,
        ...titleTokens
      },
      media: titleHeadingMediaIds,
      variant: {
        size: mappedTextSize
      },
      children: children
    });
    if (additionalInfo || link) {
      return /*#__PURE__*/_jsx(QuoteContainer, {
        marginBottom: marginBottom,
        children: quote
      });
    }
    return quote;
  };
  return /*#__PURE__*/_jsxs(BlockQuoteContainer, {
    ...selectProps(rest),
    paddingTop: paddingTop,
    paddingBottom: paddingBottom,
    paddingLeft: paddingLeft,
    paddingRight: paddingRight,
    width: width,
    backgroundGradient: backgroundGradient,
    ref: ref,
    children: [renderQuote(), (additionalInfo || link) && /*#__PURE__*/_jsxs(StackView, {
      space: 0,
      children: [link && renderLink(), additionalInfo && /*#__PURE__*/_jsx(Typography, {
        tokens: {
          color
        },
        variant: {
          size: 'small'
        },
        children: additionalInfo
      })]
    })]
  });
});
BlockQuote.displayName = 'BlockQuote';
BlockQuote.propTypes = {
  ...selectedSystemPropTypes,
  ...withLinkRouter.propTypes,
  tokens: getTokensPropType('BlockQuote'),
  variant: PropTypes.exact({
    alternative: PropTypes.bool,
    size: PropTypes.string
  }),
  /**
   * Children nodes that can be added
   */
  children: PropTypes.node.isRequired,
  /**
   * External source's identifier (e.g. author's name)
   */
  link: PropTypes.string,
  /**
   * External source's URL
   */
  linkHref: PropTypes.string,
  /**
   * To provide additional information about the source, rendered underneath `link`
   */
  additionalInfo: PropTypes.string,
  /**
   * Whether to render BlockQuote as a heading size of `h3` or large text size
   */
  textStyle: PropTypes.oneOf(['large', 'heading'])
};
export default BlockQuote;